import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { updateProfile } from "../lib/api";
import { format } from "date-fns";
import { toast } from "sonner";
import { BlobSticker, StarSticker, HeartSticker, CrescentSticker } from "../components/DecorativeShapes";
import WelcomeCarousel from "../components/WelcomeCarousel";
import BrandLogo from "../components/BrandLogo";

const GOAL_OPTIONS = [
  { value: "general",       label: "General wellness",      emoji: "✦", desc: "Track my cycle and feel more in tune" },
  { value: "fertility",     label: "Fertility tracking",    emoji: "◈", desc: "Optimize timing for conception" },
  { value: "pcos",          label: "PCOS support",          emoji: "◇", desc: "Manage symptoms and understand patterns" },
  { value: "perimenopause", label: "Perimenopause",         emoji: "◉", desc: "Navigate the transition with clarity" },
  { value: "pregnancy",     label: "Pregnancy",             emoji: "◎", desc: "Track my pregnancy week by week" },
];

export default function OnboardingPage({ onDone }) {
  const [welcomeDone, setWelcomeDone] = useState(() => localStorage.getItem("knya.welcome.done") === "1");
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [lastPeriod, setLastPeriod] = useState(null);
  const [cycleLength, setCycleLength] = useState("28");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const TOTAL_STEPS = 3;

  const steps = [
    {
      eyebrow: "Welcome",
      title: "What may I call you?",
      subtitle: "Knya softly personalizes around you. Nothing here is permanent.",
    },
    {
      eyebrow: "Your intention",
      title: "What brings you to Knya?",
      subtitle: "We'll shape your experience around this. You can always change it later.",
    },
    {
      eyebrow: "Your rhythm",
      title: "When did your last period start?",
      subtitle: "Even an estimate helps. We'll refine it together.",
    },
    {
      eyebrow: "Your cycle",
      title: "How long is your average cycle?",
      subtitle: "Most cycles are 24–35 days. You can change this any time.",
    },
  ];

  const canContinue = () => {
    if (step === 0) return name.trim().length > 0;
    if (step === 1) return !!goal;
    if (step === 2) return !!lastPeriod;
    if (step === 3) return !!cycleLength;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await updateProfile({
        name: name.trim(),
        last_period_start: format(lastPeriod, "yyyy-MM-dd"),
        average_cycle_length: parseInt(cycleLength, 10),
        average_period_length: 5,
        wellness_goal: goal,
        onboarded: true,
      });
      toast.success(`Welcome, ${name.split(" ")[0]}. Your space is ready.`);
      if (onDone) await onDone();
      else navigate("/");
    } catch {
      toast.error("Something went soft. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const next = async () => {
    if (step < 3) setStep(step + 1);
    else await submit();
  };

  // Show beautiful welcome carousel first
  if (!welcomeDone) {
    return (
      <WelcomeCarousel
        onDone={() => {
          try { localStorage.setItem("knya.welcome.done", "1"); } catch { /* */ }
          setWelcomeDone(true);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" data-testid="onboarding-page">
      {/* Subtle Bloom background */}
      <BlobSticker size={180} color="#F4C7CF" className="absolute -top-24 -right-24 opacity-30 float-slow" />
      <BlobSticker size={140} color="#D3D8E5" className="absolute top-1/3 -left-16 opacity-20 float-mid" />

      {/* Progress */}
      <div className="px-5 pt-10 relative z-10">
        <div className="flex items-center gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= step ? "bg-[hsl(var(--plum))]" : "bg-[hsl(var(--muted))]"
              }`}
            />
          ))}
        </div>
        <div className="mt-6 flex items-center gap-2">
          <BrandLogo size={32} />
          <span className="font-extrabold text-lg tracking-tight">Knya</span>
        </div>
      </div>

      <motion.div
        key={step}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32 }}
        className="px-5 mt-12 flex-1 relative z-10"
      >
        <p className="text-[10px] uppercase tracking-wider text-primary font-extrabold">
          {steps[step].eyebrow}
        </p>
        <h1
          className="text-[40px] mt-3 leading-[1.02] font-extrabold tracking-tight text-foreground"
          data-testid="onboarding-step-title"
        >
          {steps[step].title}
        </h1>
        <p className="text-[14px] text-muted-foreground mt-3.5 leading-relaxed font-medium">
          {steps[step].subtitle}
        </p>

        <div className="mt-10 space-y-4">
          {step === 0 && (
            <Input
              autoFocus
              placeholder="Your first name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-14 text-lg bg-[hsl(var(--card))] rounded-2xl border-2 px-5 text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]"
              data-testid="onboarding-name-input"
            />
          )}
          {step === 1 && (
            <div className="space-y-2">
              {GOAL_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setGoal(opt.value)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border-2 transition text-left ${
                    goal === opt.value
                      ? "bg-[hsl(var(--secondary))] border-[hsl(var(--plum))]"
                      : "bg-[hsl(var(--card))] border-[hsl(var(--border))] hover:border-[hsl(var(--plum))]/50"
                  }`}
                  data-testid={`onboarding-goal-${opt.value}`}
                >
                  <span className="text-xl w-7 text-center opacity-80">{opt.emoji}</span>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[hsl(var(--foreground))]">{opt.label}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{opt.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {step === 2 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="secondary"
                  className="w-full h-14 justify-start gap-2 rounded-2xl text-base bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                  data-testid="onboarding-last-period-button"
                >
                  <CalendarDays size={18} />
                  {lastPeriod ? format(lastPeriod, "MMMM d, yyyy") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0 w-auto">
                <Calendar
                  mode="single"
                  selected={lastPeriod}
                  onSelect={setLastPeriod}
                  disabled={(d) => d > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          )}
          {step === 3 && (
            <Select value={cycleLength} onValueChange={setCycleLength}>
              <SelectTrigger
                className="h-14 text-base rounded-2xl bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                data-testid="onboarding-cycle-length-select"
              >
                <SelectValue placeholder="Average cycle length" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 18 }, (_, i) => 21 + i).map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} days
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <p className="mt-8 text-xs text-[hsl(var(--muted-foreground))] font-body">
          We never share your data. Update or delete anything later.
        </p>
      </motion.div>

      <div
        className="px-5 pb-8 relative z-10"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 28px)" }}
      >
        <button
          className="btn-pill-dark w-full h-14 text-[15px]"
          disabled={!canContinue() || submitting}
          onClick={next}
          data-testid="onboarding-continue-button"
        >
          {submitting ? "Setting up…" : step === 3 ? "Enter Knya" : "Continue"}
          <ArrowRight size={16} />
        </button>
        {step > 0 && (
          <button
            type="button"
            className="w-full mt-3 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            onClick={() => setStep(step - 1)}
            data-testid="onboarding-back-button"
          >
            Back
          </button>
        )}
      </div>
    </div>
  );
}
